// 018: strict boundaries shared by browser, minutes and recognition. No source text in job data.
import { z } from '../kernel/zod.ts';
import { IdSchema, TimestampSchema } from '../kernel/common.ts';
import { ItemFieldSchema } from './item.ts';

export const PASTE_ANALYSIS_BODY_LIMIT = 5 * 1024 * 1024;
export const PASTE_COLUMNS_MAX = 100;
export const PASTE_POSITIONS_MAX = 100_000;
export const PASTE_SCHEMA_VERSION = 'v1';
export const PasteSourceIdSchema = z.string().min(1).max(100);
export const PasteDigestSchema = z.string().min(1).max(128);
export const PasteFieldSchema = ItemFieldSchema.refine((field) => field !== 'responsibleRaw', {
  message: 'Поле доступно только для чтения',
});
export const pastePurposes = ['columns'] as const;
export const PastePurposeSchema = z.enum(pastePurposes);
export type PastePurpose = z.infer<typeof PastePurposeSchema>;
export const pasteAnalysisStates = [
  'queued',
  'running',
  'completed',
  'manual',
  'cancelled',
  'superseded',
  'expired',
] as const;
export const PasteAnalysisStateSchema = z.enum(pasteAnalysisStates);
export type PasteAnalysisState = z.infer<typeof PasteAnalysisStateSchema>;
export const pasteReasonCodes = [
  'no_key',
  'timeout',
  'refused',
  'bad_shape',
  'disabled',
  'limit',
  'cancelled',
  'superseded',
  'expired',
  'access_revoked',
  'catalog_changed',
  'unsafe_input',
  'truncated',
  'unclear',
  'contradictory',
  'manual',
  'matched',
  'human_cache',
] as const;
export const PasteReasonCodeSchema = z.enum(pasteReasonCodes);
export type PasteReasonCode = z.infer<typeof PasteReasonCodeSchema>;
export const pasteIssueCodes = [
  'unassigned',
  'duplicate_target',
  'unconfirmed_skip',
  'ambiguous',
  'contradictory',
  'truncated',
  'limit',
  'catalog_changed',
  'invalid_value',
] as const;
export const PasteIssueCodeSchema = z.enum(pasteIssueCodes);
export type PasteIssueCode = z.infer<typeof PasteIssueCodeSchema>;
export const PasteIssueSchema = z.strictObject({
  code: PasteIssueCodeSchema,
  severity: z.enum(['info', 'warning', 'error']),
  sourceRowId: PasteSourceIdSchema.optional(),
  sourceColumnId: PasteSourceIdSchema.optional(),
  reasonCode: PasteReasonCodeSchema,
});
export type PasteIssue = z.infer<typeof PasteIssueSchema>;
const natural = z.number().int().min(0);
const revision = z.number().int().min(1);
const unique = (values: string[]) => new Set(values).size === values.length;
export const PasteCandidateSchema = z.strictObject({
  targetFieldId: PasteFieldSchema,
  probability: z.number().min(0).max(1),
});
const assignmentBase = {
  sourceColumnId: PasteSourceIdSchema,
  state: z.enum(['unassigned', 'suggested', 'needs_review', 'confirmed', 'skipped', 'conflict']),
  origin: z.enum(['rule', 'jev', 'deepseek', 'user', 'human_cache']),
  reasonCode: PasteReasonCodeSchema,
  candidates: z.array(PasteCandidateSchema).max(1100),
  decisionId: IdSchema.optional(),
  userLocked: z.boolean(),
};
export const PasteAssignmentSchema = z.discriminatedUnion('action', [
  z.strictObject({ ...assignmentBase, action: z.literal('map'), targetFieldId: PasteFieldSchema }),
  z.strictObject({ ...assignmentBase, action: z.literal('skip') }),
  z.strictObject({ ...assignmentBase, action: z.literal('unassigned') }),
]);
export type PasteAssignment = z.infer<typeof PasteAssignmentSchema>;
export const PasteMapOrSkipSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('map'),
    sourceColumnId: PasteSourceIdSchema,
    targetFieldId: PasteFieldSchema,
  }),
  z.strictObject({ kind: z.literal('skip'), sourceColumnId: PasteSourceIdSchema }),
]);
export type PasteMapOrSkip = z.infer<typeof PasteMapOrSkipSchema>;
const headerCell = z.strictObject({
  sourceRowId: PasteSourceIdSchema,
  text: z.string().max(PASTE_ANALYSIS_BODY_LIMIT),
});
const sample = headerCell.extend({ present: z.boolean() }).strict();
export const PasteSourceColumnSchema = z.strictObject({
  sourceColumnId: PasteSourceIdSchema,
  index: natural,
  headerCells: z.array(headerCell).max(3),
  samples: z.array(sample).max(12),
});
const locked = z.discriminatedUnion('action', [
  z.strictObject({
    sourceColumnId: PasteSourceIdSchema,
    action: z.literal('map'),
    targetFieldId: PasteFieldSchema,
  }),
  z.strictObject({ sourceColumnId: PasteSourceIdSchema, action: z.literal('skip') }),
]);
export const PasteAnalysisCreateBodySchema = z
  .strictObject({
    sessionId: IdSchema,
    generation: revision,
    mappingRevision: natural,
    purpose: PastePurposeSchema,
    source: z.strictObject({
      columns: z.array(PasteSourceColumnSchema).min(1).max(PASTE_COLUMNS_MAX),
      rowCount: z.number().int().min(1).max(PASTE_POSITIONS_MAX),
      columnCount: z.number().int().min(1).max(PASTE_COLUMNS_MAX),
    }),
    headerRowCount: z.number().int().min(0).max(3),
    lockedAssignments: z.array(locked).max(PASTE_COLUMNS_MAX),
  })
  .superRefine((v, ctx) => {
    const bad = (message: string) => ctx.addIssue({ code: 'custom', message });
    const cols = v.source.columns;
    const ids = new Set(cols.map((c) => c.sourceColumnId));
    if (
      cols.length !== v.source.columnCount ||
      ids.size !== cols.length ||
      !unique(cols.map((c) => String(c.index))) ||
      cols.some((c) => c.index >= v.source.columnCount) ||
      v.source.rowCount * v.source.columnCount > PASTE_POSITIONS_MAX
    )
      bad('Некорректная геометрия источника');
    if (v.headerRowCount > v.source.rowCount) bad('Заголовки выходят за исходный диапазон');
    if (
      !unique(v.lockedAssignments.map((a) => a.sourceColumnId)) ||
      v.lockedAssignments.some((a) => !ids.has(a.sourceColumnId))
    )
      bad('Некорректное назначение');
  });
export type PasteAnalysisCreateBody = z.infer<typeof PasteAnalysisCreateBodySchema>;
export const PasteAnalysisReceiptSchema = z.strictObject({
  analysisId: IdSchema,
  sessionId: IdSchema,
  generation: revision,
  purpose: PastePurposeSchema,
  state: PasteAnalysisStateSchema,
  createdAt: TimestampSchema,
  deadlineAt: TimestampSchema,
  expiresAt: TimestampSchema,
  catalogVersion: PasteDigestSchema,
  feedbackVersion: natural,
  replayed: z.boolean(),
});
export type PasteAnalysisReceipt = z.infer<typeof PasteAnalysisReceiptSchema>;
export const PasteAnalysisResultSchema = z.strictObject({
  schemaVersion: z.literal(PASTE_SCHEMA_VERSION),
  questionVersion: z.string().min(1).max(40),
  catalogVersion: PasteDigestSchema,
  assignments: z.array(PasteAssignmentSchema).max(PASTE_COLUMNS_MAX).optional(),
  headerSuggestion: z
    .strictObject({
      headerRowCount: z.number().int().min(0).max(3),
      probability: z.number().min(0).max(1),
      decisionId: IdSchema.optional(),
    })
    .optional(),
  issues: z.array(PasteIssueSchema).max(5000),
  decisionIds: z.array(IdSchema).max(5000),
});
export type PasteAnalysisResult = z.infer<typeof PasteAnalysisResultSchema>;
export const PasteAnalysisGetSchema = PasteAnalysisReceiptSchema.extend({
  reasonCode: PasteReasonCodeSchema.optional(),
  result: PasteAnalysisResultSchema.optional(),
}).strict();
export type PasteAnalysisGet = z.infer<typeof PasteAnalysisGetSchema>;
export const PasteAnalysisParamsSchema = z.strictObject({ id: IdSchema, analysisId: IdSchema });
export const PasteFeedbackBodySchema = z
  .strictObject({
    requestId: IdSchema,
    generation: revision,
    mappingRevision: natural,
    expectedFeedbackVersion: natural,
    decisions: z
      .array(
        z.strictObject({
          questionKey: z.string().min(1).max(200),
          decisionIds: z.array(IdSchema).max(5000),
          value: PasteMapOrSkipSchema,
        }),
      )
      .min(1)
      .max(100),
  })
  .refine((v) => unique(v.decisions.map((d) => d.questionKey)), {
    message: 'Вопрос указан дважды',
  });
export type PasteFeedbackBody = z.infer<typeof PasteFeedbackBodySchema>;
export const PasteFeedbackAcceptedSchema = z.strictObject({
  requestId: IdSchema,
  feedbackVersion: natural,
  acceptedMappingRevision: natural,
  decisionIds: z.array(IdSchema).max(5000),
  replayed: z.boolean(),
});
export type PasteFeedbackAccepted = z.infer<typeof PasteFeedbackAcceptedSchema>;
